import { QualityQueue } from './queue';
import { readAudio, percentile, RTT_BUCKETS } from './stats';

const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
const numberId = value => Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : null;

export class ConnectionQuality {
  constructor() {
    this.calls = new Map();
    this.previous = new Map();
    this.pendingWs = null;
    this.rtcIncident = null;
    this.offset = 0;
    this.clockSynced = false;
    this.call = null;
  }

  now() { return Math.round(Date.now() + this.offset); }

  start(socket, owner, tabId) {
    if (!owner || process.env.VUE_APP_CONNECTION_QUALITY === 'false') return;
    if (this.owner !== String(owner)) {
      this.stop();
      this.owner = String(owner);
      this.queue = new QualityQueue(owner);
      this.sessionId = tabId;
      this.tabId = tabId;
      this.calls.clear();
      this.window = this.newWindow();
    }
    this.socket = socket;
    if (!this.pagehide) {
      this.pagehide = event => { this.pageClosing(event); };
      window.addEventListener('pagehide', this.pagehide);
    }
    if (!this.timer) this.timer = setInterval(() => this.tick().catch(() => {}), 5000);
  }

  pageClosing(event) {
    this.flushWindow();
    if ((!event || !event.persisted) && this.owner && this.connectionId && this.socket && this.socket.connected && !this.pendingWs) {
      const now = this.now();
      const marker = { ...this.base(), kind: 'incident', id: `ws:${this.connectionId}`, revision: 5,
        start_ms: now, end_ms: now, last_success_ms: this.lastSuccess || null,
        channel: 'ws', category: 'planned', reason: 'client_planned', recovered: 0 };
      // Persist for the next page and also send without waiting for IndexedDB during unload.
      this.put(marker);
      try { this.socket.emit('quality:batch', [marker], () => {}); } catch (_) { /* Best effort on page exit. */ }
    }
    this.send().catch(() => {});
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    if (this.pagehide) window.removeEventListener('pagehide', this.pagehide);
    this.pagehide = null;
    this.flushWindow();
    this.socket = null;
    this.call = null;
    this.owner = null;
    this.pendingWs = null;
    this.detachPeer();
  }

  base() {
    return { v: 1, session_id: this.sessionId, tab_id: this.tabId,
      connection_id: this.connectionId || '', frontend_version: process.env.VUE_APP_VERSION || 'unknown',
      clock_synced: this.clockSynced ? 1 : 0,
      client_call_id: this.call ? this.call.id : '',
      order_id: this.call ? this.call.order_id : null,
      call_log_id: this.call ? this.call.call_log_id : null };
  }

  put(event) {
    if (this.queue) this.queue.enqueue({ ...this.base(), ...event }).catch(() => {});
  }

  connected() {
    if (!this.socket) return;
    this.flushWindow();
    if (this.pendingWs) {
      this.put({ ...this.pendingWs, revision: 4, end_ms: this.now(), recovered: 1 });
      this.pendingWs = null;
    }
    this.connectionId = this.socket.id;
    this.window = this.newWindow();
  }

  disconnected(reason, planned = false) {
    if (!this.owner || !this.connectionId || this.pendingWs) return;
    planned = planned || ["io server disconnect", "io client disconnect"].includes(reason);
    this.flushWindow();
    this.pendingWs = { ...this.base(), kind: 'incident', id: `ws:${this.connectionId}`, revision: 2,
      start_ms: this.now(), end_ms: null, last_success_ms: this.lastSuccess || null,
      channel: 'ws', category: planned ? 'planned' : 'unknown',
      reason: ['ping timeout', 'transport close', 'transport error'].includes(reason) ? reason : (planned ? 'client_planned' : 'unknown'), recovered: 0 };
    if (!planned && ['ping timeout', 'transport close', 'transport error'].includes(reason)) this.pendingWs.category = 'unexpected';
    this.put(this.pendingWs);
    this.send().catch(() => {});
  }

  newWindow() {
    return { start: this.now(), monotonic: typeof performance !== 'undefined' ? performance.now() : 0,
      probes: 0, timeouts: 0, rtts: [], variation: 0, pairs: 0, last: null,
      timer_lag_ms: 0, hidden_samples: 0, poll_count: 0, audio: {}, audioArrays: {},
      rtc_polls: 0, rtc_errors: 0, bad_ws_samples: 0, bad_audio_samples: 0 };
  }

  async tick() {
    if (this.ticking || !this.owner) return;
    this.ticking = true;
    const w = this.window;
    try {
      const monotonic = performance.now();
      const elapsed = monotonic - (this.lastTick || monotonic - 5000);
      this.lastTick = monotonic;
      w.timer_lag_ms += Math.max(0, elapsed - 5000);
      w.poll_count++;
      if (document.hidden) w.hidden_samples++;
      await Promise.all([this.probe(w), this.audio(w)]);
      if (this.window === w && performance.now() - w.monotonic >= 60000) this.flushWindow();
      await this.send();
    } finally { this.ticking = false; }
  }

  probe(w) {
    if (!this.socket || !this.socket.connected) return Promise.resolve();
    const socket = this.socket;
    const begin = performance.now();
    const wall = Date.now();
    w.probes++;
    return new Promise(resolve => {
      socket.timeout(3000).emit('quality:probe', { client_call_id: this.call ? this.call.id : '' }, (error, reply) => {
        if (error || !reply || !reply.ok) {
          w.timeouts++;
          w.bad_ws_samples++;
          w.last = null;
        } else {
          const rtt = performance.now() - begin;
          w.rtts.push(rtt);
          if (w.last !== null) { w.variation += Math.abs(rtt - w.last); w.pairs++; }
          w.last = rtt;
          if (rtt > 300) w.bad_ws_samples++;
          if (Number.isFinite(reply.server_ms) && rtt < 1000 && !this.clockSynced && !this.call) {
            // Correct future windows only; never move the boundary of an existing window.
            this.flushWindow();
            this.offset = reply.server_ms - (wall + rtt / 2);
            this.clockSynced = true;
            this.window = this.newWindow();
          }
          this.lastSuccess = this.now();
        }
        resolve();
      });
    });
  }

  async audio(w) {
    if (!this.call || !this.call.talk_start_ms || !this.peer) return;
    const peer = this.peer;
    const generation = this.call.id;
    try {
      const report = await peer.getStats();
      if (!this.call || this.call.id !== generation || this.peer !== peer || this.window !== w) return;
      const sample = readAudio(report, this.previous);
      w.rtc_polls++;
      Object.keys(sample).forEach(key => {
        if (Array.isArray(sample[key])) {
          w.audioArrays[key] = [...(w.audioArrays[key] || []), ...sample[key]];
        } else w.audio[key] = (w.audio[key] || 0) + sample[key];
      });
      const total = sample.in_received + sample.in_lost;
      if (sample.in_jitter.some(value => value > 30) || (total > 0 && sample.in_lost / total > 0.03)
        || sample.media_rtt.some(value => value > 300)) w.bad_audio_samples++;
    } catch (_) { w.rtc_errors++; }
  }

  flushWindow() {
    const w = this.window;
    if (!w || !this.queue) return;
    const duration = Math.max(0, Math.round(performance.now() - w.monotonic));
    if (duration < 1) return;
    const histogram = RTT_BUCKETS.map(() => 0);
    w.rtts.forEach(value => histogram[RTT_BUCKETS.findIndex(limit => value <= limit)]++);
    const metrics = { duration_ms: duration, probe_count: w.probes, timeout_count: w.timeouts,
      rtt_p50_ms: percentile(w.rtts, 0.5), rtt_p95_ms: percentile(w.rtts, 0.95),
      rtt_max_ms: w.rtts.length ? Math.max(...w.rtts) : null,
      rtt_sum_ms: w.rtts.reduce((sum, value) => sum + value, 0), rtt_count: w.rtts.length,
      rtt_variation_sum_ms: w.variation, rtt_variation_count: w.pairs,
      timer_lag_ms: Math.round(w.timer_lag_ms), hidden_samples: w.hidden_samples, poll_count: w.poll_count,
      rtc_polls: w.rtc_polls, rtc_errors: w.rtc_errors,
      bad_ws_samples: w.bad_ws_samples, bad_audio_samples: w.bad_audio_samples,
      queue_dropped: this.queue.dropped, ...w.audio };
    ['in_jitter', 'out_jitter', 'media_rtt'].forEach(name => {
      const values = w.audioArrays[name] || [];
      metrics[`${name}_count`] = values.length;
      metrics[`${name}_sum_ms`] = values.reduce((sum, value) => sum + value, 0);
      metrics[`${name}_max_ms`] = values.length ? Math.max(...values) : null;
    });
    this.queue.dropped = 0;
    this.put({ kind: 'window', id: uid(), revision: 1, start_ms: w.start, end_ms: w.start + duration,
      phase: this.call ? (this.call.talk_start_ms ? 'talking' : 'dialing') : 'idle', metrics, rtt_histogram: histogram });
    this.window = this.newWindow();
  }

  callLog(payload) {
    if (!this.owner || !payload.client_call_id) return;
    const id = String(payload.client_call_id);
    if (!['dial', 'ringing', 'confirmed', 'hangup'].includes(payload.type)) return;
    this.flushWindow();
    let call = this.calls.get(id);
    if (!call) {
      call = { id, start_ms: this.now(), talk_start_ms: null, end_ms: null, revision: 0,
        order_id: numberId(payload.order_id), call_log_id: numberId(payload.call_log_id) };
      this.calls.set(id, call);
      if (this.calls.size > 100) this.calls.delete(this.calls.keys().next().value);
    }
    call.call_log_id = numberId(payload.call_log_id) || call.call_log_id;
    call.order_id = numberId(payload.order_id) || call.order_id;
    if (payload.type === 'confirmed' && !call.talk_start_ms) { call.talk_start_ms = this.now(); this.previous.clear(); }
    if (payload.type === 'hangup') {
      call.end_ms = call.end_ms || this.now();
      this.closeRtc(false);
    }
    this.call = call;
    this.put({ ...call, client_call_id: id, kind: 'call', revision: ++call.revision });
    this.send().catch(() => {});
    if (call.end_ms) { this.call = null; this.detachPeer(); }
    this.window = this.newWindow();
    if (payload.type === 'confirmed') this.audio(this.window).catch(() => {});
  }

  callAck(payload, response) {
    const call = this.calls.get(String(payload.client_call_id));
    const logId = numberId(response.call_log_id || (response.log_data && response.log_data.call_log_id));
    if (!call || !logId || logId === call.call_log_id) return;
    call.call_log_id = logId;
    this.put({ ...call, client_call_id: call.id, kind: 'call', revision: ++call.revision });
  }

  attachPeer(peer) {
    if (!peer || this.peer === peer || process.env.VUE_APP_CONNECTION_QUALITY === 'false') return;
    this.detachPeer();
    this.peer = peer;
    this.previous.clear();
    this.peerHandler = () => {
      const state = peer.connectionState || peer.iceConnectionState;
      if (['disconnected', 'failed'].includes(state) && !this.rtcIncident) {
        this.rtcIncident = { ...this.base(), kind: 'incident', id: `rtc:${uid()}`, revision: 1,
          channel: 'webrtc', category: 'unexpected', reason: state, start_ms: this.now(), end_ms: null,
          last_success_ms: null, recovered: 0 };
        this.put(this.rtcIncident);
        this.send().catch(() => {});
      }
      if (['connected', 'completed'].includes(state)) this.closeRtc(true);
    };
    peer.addEventListener('connectionstatechange', this.peerHandler);
    peer.addEventListener('iceconnectionstatechange', this.peerHandler);
  }

  closeRtc(recovered) {
    if (!this.rtcIncident) return;
    this.put({ ...this.rtcIncident, revision: 3, end_ms: this.now(), recovered: recovered ? 1 : 0 });
    this.rtcIncident = null;
  }

  detachPeer() {
    if (this.peer && this.peerHandler) {
      this.peer.removeEventListener('connectionstatechange', this.peerHandler);
      this.peer.removeEventListener('iceconnectionstatechange', this.peerHandler);
    }
    this.peer = null;
    this.peerHandler = null;
    this.previous.clear();
  }

  async send() {
    if (this.sending || !this.socket || !this.socket.connected || !this.queue) return;
    this.sending = true;
    const queue = this.queue;
    try {
      const rows = await queue.batch();
      if (!rows.length) return;
      const response = await new Promise(resolve => this.socket.timeout(10000)
        .emit('quality:batch', rows.map(row => row.event), (error, ack) => resolve(error ? null : ack)));
      if (response && response.ok) await queue.remove(rows.map(row => row.key));
      else if (response && response.retryable === false) {
        await queue.remove(rows.map(row => row.key));
        queue.dropped += rows.length;
      }
    } finally { this.sending = false; }
  }
}
const collector = new ConnectionQuality();
const guarded = {};
// Telemetry must never interrupt call logging, SIP callbacks or authentication.
['start', 'stop', 'connected', 'disconnected', 'callLog', 'callAck', 'attachPeer'].forEach(method => {
  guarded[method] = (...args) => {
    try { return collector[method](...args); } catch (_) { return undefined; }
  };
});
export default guarded;
